import { createFileRoute } from "@tanstack/react-router";
import { CostPageLayout } from "@/components/cost/CostPageLayout";
import { serviceHead } from "@/lib/service-head";
import { loadPageSettings } from "@/lib/page-seo-loader";

const SLUG = "/shingle-roof-cost-phoenix";
const TITLE = "Shingle Roof Cost in Phoenix (2026) | Durafoam Roofing";
const DESCRIPTION =
  "What a new shingle roof costs in Phoenix: per-square-foot pricing for architectural shingles, what drives the number, and how to get a roof built for AZ heat.";

const faqs = [
  { q: "How much does a shingle roof cost in Phoenix?", a: "Architectural shingle replacements typically run $4.50–$7.50 per square foot installed. A 2,000 sq ft roof usually lands in the $9,000–$15,000 range, including tear-off, underlayment, shingles, and flashings." },
  { q: "How long do shingles last in Phoenix?", a: "Standard 3-tab: 12–18 years. Architectural shingles: 18–25 years. AZ sun shortens manufacturer ratings by roughly 25–30% vs. cooler climates — we'll be honest with you about realistic life." },
  { q: "Should I use 3-tab or architectural?", a: "Architectural every time in Phoenix. The small upgrade in cost buys meaningfully better heat and wind performance. We don't recommend 3-tab on Valley homes." },
  { q: "Do I need a tear-off?", a: "Almost always in Phoenix. AZ code limits roof-overs and old shingles trap heat. Tear-off is included in our standard quotes." },
];

export const Route = createFileRoute("/shingle-roof-cost-phoenix")({
  loader: () => loadPageSettings(SLUG),
  head: ({ loaderData }) =>
    serviceHead({ override: loaderData?.seo,
      slug: SLUG,
      title: TITLE,
      description: DESCRIPTION,
      serviceName: "Shingle Roof Replacement",
      faqs,
      rating: loaderData?.rating,
      reviewCount: loaderData?.reviewCount,
    }),
  component: Page,
});

function Page() {
  return (
    <CostPageLayout
      pageSlug="shingle-roof-cost-phoenix"
      eyebrow="Phoenix cost guide"
      h1="Shingle Roof Cost in Phoenix"
      subhead="Honest 2026 price ranges for architectural shingle roof replacements in the Phoenix Valley."
      heroBullets={[
        "Typical range: $4.50–$7.50 per sq ft",
        "Most 2,000 sq ft roofs: $9,000–$15,000",
        "Architectural shingles built for AZ heat",
        "Includes tear-off, underlayment, flashings",
      ]}
      intro={
        <>
          <p>
            A new architectural shingle roof in Phoenix typically costs{" "}
            <strong>$4.50–$7.50 per square foot installed</strong>, which puts most single-family
            homes in the <strong>$9,000–$15,000</strong> range. That includes tear-off of the
            existing roof, new underlayment, drip edge, starter strip, ridge cap, and flashings —
            not just shingles.
          </p>
          <p>
            Phoenix sun ages shingles faster than the manufacturer's national rating. We spec
            shingle systems that hold up to 115° rooftop temperatures and high UV.
          </p>
        </>
      }
      pricingRows={[
        { label: "Small home (≤1,500 sq ft roof)", range: "$6,500–$10,500", notes: "Standard architectural" },
        { label: "Typical home (2,000 sq ft roof)", range: "$9,000–$15,000", notes: "Full tear-off & replace" },
        { label: "Larger home (2,500–3,000 sq ft)", range: "$13,000–$22,000", notes: "Standard system" },
        { label: "Premium shingle upgrade", range: "+$0.40–$1.00 / sq ft", notes: "Designer / 50-yr shingles" },
        { label: "Synthetic underlayment", range: "+$0.30–$0.60 / sq ft", notes: "Recommended for AZ" },
      ]}
      factors={[
        { title: "Shingle quality", body: "30-yr architectural is the base. 50-year and designer shingles cost more but hold up better to Phoenix UV." },
        { title: "Roof pitch", body: "Steeper roofs (8/12+) require safety staging and slow installation. Most Phoenix homes are 4/12–6/12, the affordable range." },
        { title: "Underlayment", body: "Synthetic underlayment outlasts felt in AZ heat. The upgrade is small money for years of added life." },
        { title: "Flashings & accessories", body: "Galvanized vs. painted, ridge venting, pipe boots — small line items that add up. We itemize so you see them." },
      ]}
      faqs={faqs}
      ctaTitle="Get a real number for your shingle roof"
      ctaBody="Use the instant calculator, or schedule a free inspection — we'll measure, photograph the deck, and give you a written quote."
    />
  );
}
